import {
    cloneElement,
    isValidElement,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
} from "react";

import type {
    ContextMenuConfig,
    ContextMenuTriggerType,
} from "../types/contextMenu.types";
import { getSafeMenuPosition } from "../layout/contextMenu.layout";
import { useContextMenuRoot } from "./ContextMenuRoot";

type ContextMenuTriggerProps<TContext> = {
    children: ReactNode;
    menu: ContextMenuConfig<TContext>;
    context: TContext;
    trigger?: ContextMenuTriggerType;
    asChild?: boolean;
    className?: string;
};

export function ContextMenuTrigger<TContext>({
    children,
    menu,
    context,
    trigger = "right-click",
    asChild = true,
    className,
}: ContextMenuTriggerProps<TContext>) {
    const { openMenu } = useContextMenuRoot<TContext>();

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        // event.stopPropagation();

        openMenu({
            menu,
            context,
            position: getSafeMenuPosition({
                position: {
                    x: event.clientX,
                    y: event.clientY,
                },
            }),
        });
    };

    const triggerProps =
        trigger === "right-click"
            ? { onContextMenu: handleOpen }
            : { onClick: handleOpen };

    if (asChild && isValidElement(children)) {
        const child = children as ReactElement<Record<string, unknown>>;

        return cloneElement(child, {
            ...triggerProps,
            className: [child.props.className, className].filter(Boolean).join(" "),
        });
    }

    return (
        <div className={className} {...triggerProps}>
            {children}
        </div>
    );
}